'use client'

import { useMemo, useEffect, useState } from 'react'
import { Launch as ExternalLink } from '@carbon/icons-react'

import { useMounted } from '@/lib/opendocs/hooks/use-mounted'
import { TableOfContents } from '@/lib/opendocs/utils/toc'
import { BounceSidebar } from '@/components/motion/bounce-sidebar'
import { Separator } from '@/components/ui/separator'
import { siteConfig } from '@/config/site'
import { cn } from '@/lib/utils'

interface DefaultTableOfContentItemsProps {
  sourceFilePath: string

  messages: {
    onThisPage: string
    editPageOnGitHub: string
    startDiscussionOnGitHub: string
  }
}

interface DashboardTableOfContentsProps extends DefaultTableOfContentItemsProps {
  toc: TableOfContents
}

export function DashboardTableOfContents({
  toc,
  messages,
  sourceFilePath,
}: DashboardTableOfContentsProps) {
  const itemIds = useMemo(
    () =>
      toc.items
        ? toc.items
            .flatMap((item) => [item.url, item?.items?.map((item) => item.url)])
            .flat()
            .filter(Boolean)
            .map((id) => id?.split('#')[1])
        : [],
    [toc]
  )

  const mounted = useMounted()
  const activeHeading = useActiveItem(itemIds as string[])

  if (!toc?.items || !mounted) {
    return (
      <div className="space-y-2">
        <DefaultTableOfContentItems
          messages={messages}
          sourceFilePath={sourceFilePath}
        />
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <p className="font-medium">{messages.onThisPage}</p>

      {/* beUI Bounce Sidebar, driven by the scroll-spy: the dot springs
          between headings as the reader scrolls, and anchor hrefs handle the
          jump on click (scroll-spy then lands the dot). */}
      <BounceSidebar
        ariaLabel={messages.onThisPage}
        items={flattenToc(toc)}
        value={(activeHeading as string) ?? undefined}
        listClassName="gap-0.5 pl-5"
        itemClassName="min-h-7 rounded-md text-sm font-normal"
        indicatorClassName="left-1.5 bg-emerald-600 dark:bg-primary-active"
      />

      <div className="py-4">
        <Separator />
      </div>

      <DefaultTableOfContentItems
        messages={messages}
        sourceFilePath={sourceFilePath}
      />
    </div>
  )
}

function DefaultTableOfContentItems({
  messages,
  sourceFilePath,
}: DefaultTableOfContentItemsProps) {
  return (
    // Both links are built from the GitHub org URL, which currently 404s, so
    // they would send every docs reader to a dead page. They return as soon as
    // siteConfig marks the profile as existing.
    <div className="mt-2 flex flex-col gap-1">
      {siteConfig.links.github.exists && messages.editPageOnGitHub && (
        <a
          className="text-muted-foreground hover:text-foreground flex items-center gap-2 transition"
          href={`${siteConfig.links.github.url}/edit/main/apps/content/${sourceFilePath}`}
        >
          {messages.editPageOnGitHub} <ExternalLink size={12} />
        </a>
      )}

      {siteConfig.links.github.exists && messages.startDiscussionOnGitHub && (
        <a
          className="text-muted-foreground hover:text-foreground flex items-center gap-2 transition"
          href={`${siteConfig.links.github.url}/discussions/new/choose`}
        >
          {messages.startDiscussionOnGitHub} <ExternalLink size={12} />
        </a>
      )}
    </div>
  )
}

function useActiveItem(itemIds: string[]) {
  const [activeId, setActiveId] = useState<string | null>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id)
          }
        })
      },
      { rootMargin: `0% 0% -80% 0%` }
    )

    itemIds?.forEach((id) => {
      const element = document.getElementById(id)

      if (element) {
        observer.observe(element)
      }
    })

    return () => {
      itemIds?.forEach((id) => {
        const element = document.getElementById(id)

        if (element) {
          observer.unobserve(element)
        }
      })
    }
  }, [itemIds])

  return activeId
}

/**
 * The bounce sidebar wants a flat list, so the two heading levels the old
 * tree rendered (level < 3) are flattened; depth-2 entries keep their
 * indent inside the label, leaving the left rail free for the dot's arc.
 */
function flattenToc(toc: TableOfContents) {
  const flat: { id: string; href: string; label: React.ReactNode }[] = []

  for (const item of toc.items ?? []) {
    const id = item.url?.split('#')[1]
    if (!id) continue
    flat.push({ id, href: item.url, label: item.title })

    for (const child of item.items ?? []) {
      const childId = child.url?.split('#')[1]
      if (!childId) continue
      flat.push({
        id: childId,
        href: child.url,
        label: <span className={cn('block pl-3')}>{child.title}</span>,
      })
    }
  }

  return flat
}
